import  { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import { Clock, AlertTriangle, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import { format,isAfter, isBefore, isSameDay } from "date-fns";
import Alerts from "./Alerts";

const apiUrl = process.env.REACT_APP_API_URL;

const CustomDateRangePicker = ({ 
  isOpen, 
  onClose, 
  onApply,
  calendarRef 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [isSelectingStart, setIsSelectingStart] = useState(true);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'MMM dd, yyyy');
  };

  const handleDateSelect = (date) => {
    if (isSelectingStart || !tempStartDate) {
      setTempStartDate(date);
      setTempEndDate(null);
      setIsSelectingStart(false);
    } else {
      if (isBefore(date, tempStartDate)) {
        setTempStartDate(date);
        setTempEndDate(null);
      } else {
        setTempEndDate(date);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && isSameDay(date, today);
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    return (isAfter(date, tempStartDate) || isSameDay(date, tempStartDate)) &&
           (isBefore(date, tempEndDate) || isSameDay(date, tempEndDate));
  };

  const isStartDate = (date) => {
    return tempStartDate && date && isSameDay(date, tempStartDate);
  };

  const isEndDate = (date) => {
    return tempEndDate && date && isSameDay(date, tempEndDate);
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      const formattedStart = formatDate(tempStartDate);
      const formattedEnd = formatDate(tempEndDate);
      onApply(`custom:${formattedStart}:${formattedEnd}`);
      onClose();
    }
  };

  const clearSelection = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setIsSelectingStart(true);
  };

  const days = getDaysInMonth(currentMonth);

  if (!isOpen) return null;

  return (
    <div
      ref={calendarRef}
      className="absolute top-10 right-0 z-20 bg-white rounded-lg shadow-lg border border-gray-200 w-72"
    >
      <div className="flex items-center justify-between p-2 border-b border-gray-100">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="p-1 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronLeft className="w-3 h-3 text-gray-600" />
        </button>
        
        <span className="font-medium text-gray-900 text-sm">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="p-1 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronRight className="w-3 h-3 text-gray-600" />
        </button>
      </div>

      <div className="px-2 py-1 bg-gray-50 text-xs text-gray-700">
        {isSelectingStart || !tempStartDate ? 
          'Select start date' : 
          (tempEndDate ? 
            `${formatDisplayDate(formatDate(tempStartDate))} — ${formatDisplayDate(formatDate(tempEndDate))}` :
            `Start: ${formatDisplayDate(formatDate(tempStartDate))} • Select end date`
          )
        }
      </div>

      <div className="grid grid-cols-7 gap-1 p-1 border-b border-gray-100">
        {daysOfWeek.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 p-1">
        {days.map((date, index) => {
          const isStart = isStartDate(date);
          const isEnd = isEndDate(date);
          const inRange = isInRange(date);
          const today = isToday(date);
          
          return (
            <button
              key={index}
              type="button"
              onClick={() => date && handleDateSelect(date)}
              disabled={!date}
              className={`
                h-7 w-7 text-xs rounded-md transition-colors flex items-center justify-center
                ${!date ? 'invisible' : ''}
                ${isStart || isEnd
                  ? 'text-white font-medium' 
                  : inRange
                  ? 'text-gray-900'
                  : today
                  ? 'bg-gray-100 text-gray-900 font-medium border border-gray-300'
                  : 'text-gray-700 hover:bg-gray-50'
                }
              `}
              style={{
                backgroundColor: isStart || isEnd ? '#003366' : inRange ? '#e6f0ff' : undefined
              }}
            >
              {date?.getDate()}
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center p-2 border-t border-gray-100">
        <button
          type="button"
          onClick={clearSelection}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Clear
        </button>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={applyDateRange}
            disabled={!tempStartDate || !tempEndDate}
            className="text-xs text-white px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: !tempStartDate || !tempEndDate ? '#9ca3af' : '#003366',
              borderColor: '#003366'
            }}
            onMouseEnter={(e) => {
              if (!e.target.disabled) {
                e.target.style.backgroundColor = '#004080';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.target.disabled) {
                e.target.style.backgroundColor = '#003366';
              }
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#003366",
      light: "#1a4d80",
      dark: "#001a33",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0066cc",
      light: "#3385d6",
      dark: "#004d99",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#003366",
      secondary: "#6c757d",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 4,
        },
      },
    },
  },
});

const Container = styled(Box)({
  padding: "16px 20px",
  backgroundColor: "#ffffff",
});

const CustomFormControl = styled(FormControl)({
  minWidth: 120,
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    color: "#808080",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#003366",
    },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    backgroundColor: "#ffffff",
    height: 36,
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#cccccc",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#003366",
      borderWidth: 1,
    },
  },
  "& .MuiSelect-select": {
    fontSize: "0.8rem",
    color: "#003366",
    fontWeight: 500,
    padding: "8px 12px",
  },
});

const CustomMenuItem = styled(MenuItem)({
  fontSize: "0.8rem",
  color: "#003366",
  padding: "6px 14px",
  minHeight: "auto",
  "&:hover": {
    backgroundColor: "rgba(0, 51, 102, 0.05)",
  },
  "&.Mui-selected": {
    backgroundColor: "#f0f4f8",
    "&:hover": {
      backgroundColor: "#e6f0f7",
    },
  },
});

const PerformanceFilter = ({
  selectedAccount,
  selectedRegion,
  selectedTime,
  onAccountChange,
  onRegionChange,
  onTimeChange,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const calendarRef = useRef(null);
  const [error, setError] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [viewAlerts, setViewAlerts] = useState(false);
  let accessToken = useSelector((state) => state.accessToken.value);

  const timeOptions = [
    { value: "1h", label: "1hr" },
    { value: "3h", label: "3hr" },
    { value: "12h", label: "12hr" },
    { value: "1d", label: "1d" },
    { value: "3d", label: "3d" },
    { value: "1w", label: "1w" },
    { value: "custom", label: "Custom" },
  ];

  const fetchData = async () => {
    try {
      const queryAws = `
      query MyQuery {
        getAWSAccounts {
          AccountName
          AccountNo
          Status
          id
        }
      }
    `;

      await axios
        .post(
          apiUrl,
          { query: queryAws },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          if (response.data.data && response.data.data.getAWSAccounts)
            setAccountOptions(
              response.data.data.getAWSAccounts.filter((acc) => {
                return acc.Status === "active";
              })
            );
          console.log(response.data.data.getAWSAccounts);
          // onAccountChange(response.data.data.getAWSAccounts[0].AccountNo);
          onAccountChange(952208807839);
        })
        .catch((error) => {
          console.log(error);
        });

      const query = `
    query GetDetails($id: String!) {
      getDetails(id: $id) {
        id
        oem
        region
        environment
        product
      }
    }
  `;

      const variables = {
        id: "1",
      };
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError("Unauthorized");
      if (response.data.data.getDetails) {
        const configurationDetails = JSON.parse(response.request.response);
        const region = JSON.parse(configurationDetails.data.getDetails.region);
        const regionList = [...Object.keys(region)];
        setRegionOptions(regionList);
        if (regionList.length > 0) {
          // onRegionChange(regionList[0]);
          onRegionChange("us-east-1");
        }
        if (!selectedTime) {
          onTimeChange("1h");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const api = useMemo(
    () =>
      axios.create({
        baseURL: apiUrl,
        timeout: 30000,
        headers: {
          Authorization: accessToken,
        },
      }),
    [accessToken]
  );

  useEffect(() => {
    if (selectedTime && selectedTime.startsWith("custom:")) {
      const [, startIso, endIso] = selectedTime.split(":");
      try {
        setStartDate(new Date(startIso));
        setEndDate(new Date(endIso));
      } catch (error) {
        console.error("Error parsing custom date range:", error);
      }
    }
  }, [selectedTime]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTimeOptionClick = (timeValue) => {
    if (timeValue === "custom") {
      setShowCalendar(true);
    } else {
      onTimeChange(timeValue);
      setShowCalendar(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCustomDateRange = () => {
    if (selectedTime && selectedTime.startsWith("custom:")) {
      const [, startDateStr, endDateStr] = selectedTime.split(":");
      try {
        return `${formatDateForDisplay(startDateStr)} - ${formatDateForDisplay(endDateStr)}`;
      } catch (error) {
        console.error("Error formatting custom date range:", error);
        return "Custom Range";
      }
    }
    return "Custom";
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <CustomFormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Account</InputLabel>
              <Select
                value={selectedAccount || ""}
                onChange={(e) => onAccountChange(e.target.value)}
                label="Account"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflow: 'auto',
                    },
                    sx: {
                      borderRadius: 1,
                      marginTop: 1,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                }}
              >
                {accountOptions.map((account) => (
                  <CustomMenuItem key={account.id} value={account.AccountNo}>
                    {account.AccountName.toLowerCase()}
                  </CustomMenuItem>
                ))}
              </Select>
            </CustomFormControl>

            <CustomFormControl size="small">
              <InputLabel>Region</InputLabel>
              <Select
                value={selectedRegion || ""}
                onChange={(e) => onRegionChange(e.target.value)}
                label="Region"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflow: 'auto',
                    },
                    sx: {
                      borderRadius: 1,
                      marginTop: 1,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                      },
                    },
                  },
                }}
              >
                {regionOptions.map((region) => (
                  <CustomMenuItem key={region} value={region}>
                    {region}
                  </CustomMenuItem>
                ))}
              </Select>
            </CustomFormControl>

            <div className="relative">
              <Box
                sx={{
                  display: "flex",
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  p: 0.5,
                  alignItems: "center",
                }}
              >
                <Clock className="h-3 w-3 text-[#003366] ml-2 mr-1" />
                {timeOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={() => handleTimeOptionClick(option.value)}
                    variant={
                      selectedTime === option.value ||
                      (option.value === "custom" &&
                        selectedTime &&
                        selectedTime.startsWith("custom:"))
                        ? "contained"
                        : "text"
                    }
                    size="small"
                    sx={{
                      mx: 0.25,
                      textTransform: "none",
                      minWidth: "auto",
                      height: 32,
                      fontSize: "0.75rem",
                      bgcolor:
                        selectedTime === option.value ||
                        (option.value === "custom" &&
                          selectedTime &&
                          selectedTime.startsWith("custom:"))
                          ? "primary.main"
                          : "transparent",
                      "&:hover": {
                        bgcolor:
                          selectedTime === option.value ||
                          (option.value === "custom" &&
                            selectedTime &&
                            selectedTime.startsWith("custom:"))
                            ? "primary.dark"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {option.value === "custom" &&
                    selectedTime &&
                    selectedTime.startsWith("custom:")
                      ? formatCustomDateRange()
                      : option.label}
                  </Button>
                ))}
              </Box>

              <CustomDateRangePicker
                isOpen={showCalendar}
                onClose={() => setShowCalendar(false)}
                onApply={onTimeChange}
                calendarRef={calendarRef}
              />
            </div>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <button
              onClick={() => setViewAlerts(true)}
              className="flex items-center gap-2 text-[#003366] hover:text-[#004488] px-3 py-2 rounded-lg hover:bg-[#003366]/5 transition-colors duration-200"
            >
              <AlertTriangle size={20} />
              <span className="text-sm font-medium">Active Alarms</span>
              <ArrowUpRight size={16} />
            </button>
          </Box>
        </Box>
      </Container>
      <Alerts
        open={viewAlerts}
        onClose={() => setViewAlerts(false)}
        api={api}
      />
    </ThemeProvider>
  );
};

export default PerformanceFilter;